import React, { useState } from "react";
import Reveal from "@/components/Reveal";

const territories = [
  "Luxury Hospitality",
  "Tourism & Destinations",
  "Cultural Institutions",
  "Retail & Maisons",
  "Exceptional Experiences",
  "Other",
];

export default function PrivateInquiry() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    institution: "",
    role: "",
    email: "",
    territory: "",
    intent: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    // Static site: no backend. Simulate courteous receipt.
    setSubmitted(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  return (
    <div data-testid="page-inquiry" className="pt-32">
      {/* Title */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="font-eyebrow opacity-60">Correspondence</p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <Reveal>
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              — By Appointment
            </p>
            <h1
              className="title-caps leading-[0.9] mt-8"
              style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }}
            >
              PRIVATE
              <br />
              <span className="font-script" style={{ color: "#8C6B3F", textTransform: "none", letterSpacing: 0, fontSize: "1.25em" }}>
                Inquiry.
              </span>
            </h1>
            <p className="font-body opacity-80 mt-12 max-w-xl text-lg leading-relaxed">
              We work with a limited number of brands, houses, destinations and
              institutions each year. Introductions are made by request. Every
              letter is read personally.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="hairline max-w-[1440px] mx-auto my-16" />

      {/* Form / Confirmation */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-24 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <p className="font-eyebrow opacity-60">Correspondence Card</p>
          <p className="font-serif-italic mt-6 leading-snug" style={{ fontSize: "1.5rem" }}>
            Please leave a note. A member of The House will reply within seven
            days.
          </p>
          <div className="hairline my-10" />
          <p className="font-eyebrow opacity-60">Bureaus</p>
          <ul className="mt-6 space-y-3 font-body">
            <li>Riyadh — Kingdom of Saudi Arabia</li>
            <li>Paris — France</li>
            <li>Milan — Italy</li>
            <li>London — United Kingdom</li>
          </ul>
          <div className="hairline my-10" />
          <p className="font-eyebrow opacity-60">Correspondence</p>
          <a
            href="mailto:private@nextinluxury.com"
            data-testid="inquiry-email-link"
            className="editorial-link font-serif-italic mt-4 inline-block"
            style={{ fontSize: "1.2rem" }}
          >
            private@nextinluxury.com
          </a>
        </div>

        <div className="col-span-12 md:col-span-7 md:col-start-6">
          {submitted ? (
            <Reveal className="py-16" data-testid="inquiry-confirmation">
              <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                — Received
              </p>
              <h2
                className="font-serif-display mt-8 leading-[1.05]"
                style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
              >
                Your letter is with us,{" "}
                <span className="font-serif-italic">
                  {form.firstName || "friend"}
                </span>
                .
              </h2>
              <p className="font-body opacity-80 mt-10 text-lg leading-relaxed max-w-xl">
                Every correspondence is read by a member of The House and
                answered personally. Expect a reply within seven days. Until
                then — welcome to Next in Luxury.
              </p>
            </Reveal>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="inquiry-form"
              className="space-y-12"
            >
              <div>
                <p className="font-eyebrow opacity-60 mb-8">§ 01 — The Sender</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  <div>
                    <label className="font-eyebrow opacity-50">First name</label>
                    <input
                      required
                      className="field"
                      value={form.firstName}
                      onChange={set("firstName")}
                      data-testid="inquiry-firstname"
                      placeholder="Given name"
                    />
                  </div>
                  <div>
                    <label className="font-eyebrow opacity-50">Family name</label>
                    <input
                      required
                      className="field"
                      value={form.lastName}
                      onChange={set("lastName")}
                      data-testid="inquiry-lastname"
                      placeholder="Family name"
                    />
                  </div>
                  <div>
                    <label className="font-eyebrow opacity-50">Institution</label>
                    <input
                      className="field"
                      value={form.institution}
                      onChange={set("institution")}
                      data-testid="inquiry-institution"
                      placeholder="House, hotel, maison, ministry…"
                    />
                  </div>
                  <div>
                    <label className="font-eyebrow opacity-50">Role</label>
                    <input
                      className="field"
                      value={form.role}
                      onChange={set("role")}
                      data-testid="inquiry-role"
                      placeholder="Founder, Director, Chair…"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="font-eyebrow opacity-50">Private email</label>
                    <input
                      required
                      type="email"
                      className="field"
                      value={form.email}
                      onChange={set("email")}
                      data-testid="inquiry-email"
                      placeholder="For our reply"
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="font-eyebrow opacity-60 mb-8">§ 02 — Territory</p>
                <div className="flex flex-wrap gap-3">
                  {territories.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setForm((f) => ({ ...f, territory: t }))}
                      data-testid={`inquiry-territory-${t.replace(/\s+/g, "-").toLowerCase()}`}
                      className="font-eyebrow px-5 py-3 transition-colors"
                      style={{
                        border: "1px solid #14140F",
                        background: form.territory === t ? "#14140F" : "transparent",
                        color: form.territory === t ? "#F4EFE7" : "#14140F",
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-eyebrow opacity-60 mb-8">§ 03 — The Letter</p>
                <label className="font-eyebrow opacity-50">
                  A note to The House
                </label>
                <textarea
                  required
                  rows={6}
                  className="field resize-none"
                  value={form.intent}
                  onChange={set("intent")}
                  data-testid="inquiry-intent"
                  placeholder="Tell us, in your own words, what you are ready to become."
                />
              </div>

              <div className="flex justify-between items-center">
                <p className="font-eyebrow opacity-50 max-w-xs">
                  All correspondence is treated with the utmost discretion.
                </p>
                <button
                  type="submit"
                  className="btn-ghost"
                  data-testid="inquiry-submit"
                >
                  Send the letter <span className="arrow">→</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
